<template>
  <v-card
    :loading="loading"
    class="my-2 d-flex flex-column"
    width="100%"
    height="100%"
    @[installed&&`click`]="goToApp"
  >
    <component
      :is="configScreenComponent"
      v-if="configDialogVisible"
      :params="configDialogParams"
      :visible="configDialogVisible"
      :title="app.title()"
      :update="updateConfigDialog"
      @close="closeConfigDialog"
      @save="saveConfigDialog"
    />
    <v-card-text class="text--primary">
      <v-row>
        <v-list-item-avatar
          class="pa-2 ma-3"
          tile
          size="64"
        >
          <v-img
            contain
            :src="`/assets/apps/${app.id}${app.hasDarkIcon && $vuetify.theme.dark ? '_dark' : ''}.png`"
          />
        </v-list-item-avatar>
        <div>
          <v-card-title class="px-0 mx-0 pb-0">
            {{ app.title() }}
          </v-card-title>
          <v-chip
            v-for="category in categories"
            :key="category"
            x-small
            outlined
            label
            class="mr-1"
          >
            {{ category }}
          </v-chip>
          <v-chip
            v-if="installed"
            class="ml-1 px-2"
            color="success"
            x-small
            label
          >
            <v-icon
              x-small
              class="mr-1"
            >
              mdi-check
            </v-icon> {{ $t('label.app_installed') }}
          </v-chip>
        </div>
      </v-row>

      <div class="pt-2">
        {{ app.description() }}
      </div>
    </v-card-text>
    <v-spacer />
    <v-divider class="mx-4" />
    <v-card-actions>
      <v-btn
        v-if="installed && app.configComponent()"
        :disabled="configuring || installing || uninstalling"
        color="primary"
        text
        @click.stop="configure()"
      >
        {{ $t('button.configure_app') }}
      </v-btn>
      <v-spacer />
      <v-btn
        v-if="installing || uninstalling"
        disabled
      >
        {{ uninstalling ? $t('label.app_removing') : $t('label.app_installing') }}
      </v-btn>
      <paywall-button
        v-else-if="!installed"
        :disabled="configuring"
        color="success"
        outlined
        small
        :unlocked="installable"
        @click.stop="install"
      >
        {{ $t('button.install_app') }}
      </paywall-button>
      <v-btn
        v-else
        color="error"
        text
        :disabled="configuring"
        @click.stop="uninstall"
      >
        {{ $t('button.remove_app') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script setup lang="ts">
import { computed, PropType, ref } from 'vue';
import GstplnApp from '@/components/apps/app';
import { useRouter } from 'vue-router/composables';
import store from '@/store/store';
import App from '@/grpc-api/model/app';
import { actionShowError } from '@/services/vue-utils';
import i18n from '@/plugins/i18n';
import PaywallButton from '@/components/buttons/PaywallButton.vue';

const router = useRouter();

const props = defineProps({
  app: { type: Object as PropType<GstplnApp>, required: true },
});

const installable = computed(() => {
  const { underPaywall } = props.app;
  const allowed = store.getters.allowedApps.includes(props.app.id);
  return !underPaywall || allowed;
});

const loading = ref(false);

const installing = ref(false);

const uninstalling = ref(false);

const configDialogVisible = ref(false);

const configDialogParams = ref('');

const configScreenComponent = computed(() => `${props.app.configComponent()}`);

const configuring = computed(() => configDialogVisible.value);

const installed = computed(() => props.app.installed);

const categories = computed(() => props.app.categories.map((c) => i18n.tc(`code.app_category.${c}`)));

const uninstall = async () => {
  try {
    uninstalling.value = true;
    loading.value = true;
    await store.dispatch('updateApp', { gstplnAppId: props.app.id, install: false });
  } catch (e: any) {
    actionShowError(e);
  } finally {
    loading.value = false;
    uninstalling.value = false;
  }
};

const configure = async () => {
  if (!props.app.configComponent()) return;

  props.app.loadConfigComponent();

  try {
    loading.value = true;
    const app = await store.dispatch('getApp', { gstplnAppId: props.app.id, install: true }) as App;
    configDialogParams.value = app.params ?? '';
    configDialogVisible.value = true;
  } catch (e: any) {
    actionShowError(e);
    installing.value = false; // install finish if called from install
  } finally {
    loading.value = false;
  }
};

const install = async () => {
  if (props.app.configComponent() && props.app.configBeforeInstall) {
    installing.value = true;
    configure();
    return;
  }

  try {
    loading.value = true;
    const app = await store.dispatch('updateApp', { gstplnAppId: props.app.id, install: true });
    configDialogParams.value = app.params ?? '';
  } catch (e: any) {
    console.log('install error: ', e);
    actionShowError(e);
  } finally {
    loading.value = false;
    installing.value = false;
  }
};

const closeConfigDialog = () => {
  console.log('closeConfigDialog');
  configDialogVisible.value = false;
  installing.value = false; // install finish if configure called from install
};

const saveConfigDialog = async (params?: string, close = true) => {
  try {
    loading.value = true;
    const app = await store.dispatch('updateApp', { gstplnAppId: props.app.id, install: true, params });
    configDialogParams.value = app.params ?? '';
    if (close) closeConfigDialog();
  } catch (e: any) {
    actionShowError(e);
  } finally {
    loading.value = false;
  }
  return configDialogParams.value;
};

const updateConfigDialog = async (params?: string) => saveConfigDialog(params, false);

const goToApp = async (force = false) => {
  if (!installed.value && !force) { return; }
  if (props.app.configComponent()) {
    configure();
    return;
  }
  router.push(props.app.appConfigRoute || props.app.url);
};

defineExpose({ goToApp });

</script>
